/* eslint-disable @typescript-eslint/ban-ts-comment */
import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { ApiClientConfig, ApiWithErrorResponse, ExtendedAxiosRequestConfig } from '../types';
import { getTokenHeader } from './Request';
import { ApiError } from '../errors';
import PubSub from 'pubsub-js/src/pubsub';

const APP = import.meta.env.NX_APP;

// Create a factory function to generate API clients
export const createApiClient = (config: ApiClientConfig) => {
  const apiInstance = axios.create({
    baseURL: config.baseURL
  });
  // axiosRetry(apiInstance, { retries: 3 });
  apiInstance.interceptors.request.use(async (requestConfig: InternalAxiosRequestConfig) => {
    try {
      if (config.useAppHeader) {
        // Añadir el header de la aplicación
        requestConfig.headers['x-application'] = APP;
      }
      if (config.useLocalStorageHeaders) {
        const localStorageHeaders = (import.meta.env.NX_REQUEST_LOCALSTORAGE_HEADERS || '')
          .split(',')
          .filter(Boolean)
          .reduce((index, headerConfig) => {
            const [headerName, localStorageKey] = headerConfig.split(':');
            return {
              ...index,
              [headerName]: localStorage.getItem(localStorageKey)
            };
          }, {});
        // Añadir los headers del localStorage a la petición
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        requestConfig.headers = {
          ...requestConfig.headers,
          ...localStorageHeaders
        };
      }
      if (config.useAuth) {
        const tokenHeader = await getTokenHeader();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        requestConfig.headers = {
          ...requestConfig.headers,
          ...tokenHeader
        };
      }
      const request = requestConfig as ExtendedAxiosRequestConfig<never>;
      // log url, method, params, and data for debugging
      console.log(
        `Request ${request.apiId}: ${requestConfig.method?.toUpperCase()} ${requestConfig.url}`,
        requestConfig.params,
        requestConfig.data
      );
    } catch (error) {
      console.error('Error al obtener el token de autenticación:', error);
    }

    return requestConfig;
  });

  apiInstance.interceptors.response.use((response: AxiosResponse<ApiWithErrorResponse>) => {
    const isStatusOk = response.status >= 200 && response.status < 300;
    if (!isStatusOk) {
      // throw buildRequestError(response);
    }

    const config = response.config as ExtendedAxiosRequestConfig<never>;
    console.log(`Response ${config.apiId}: ${response.status} ${response.statusText}`, response.data);
    return response;
  });

  const handleError: <T>({
    response,
    error,
    data
  }: {
    response: AxiosResponse<T>;
    error?: unknown;
    data?: T;
  }) => void = ({ response, error, data }) => {
    let code = 500;
    if (response) {
      code = response.status;
      if (response.status == 401) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        PubSub.publish(import.meta.env.NX_PUB_SUB_TOPIC_SIGN_OUT);
      }
      if (response.status >= 300) {
        return new ApiError(response, response);
      }
    }
    if(error){
      // @ts-ignore
      if(error.response) {
        // @ts-ignore
        const response = error.response as AxiosResponse<T>;
        code = response.status;
        if (response.status == 401) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          PubSub.publish(import.meta.env.NX_PUB_SUB_TOPIC_SIGN_OUT);
        }

      }
    }
    return new ApiError({}, code);
  };

  // Return the API client methods
  return {
    get: async <T extends ApiWithErrorResponse, D = unknown>(
      url: string,
      config: ExtendedAxiosRequestConfig<D>
    ): Promise<AxiosResponse<T>> => {
      try {
        const response = await apiInstance.get(url, config);
        return response;
      } catch (error: unknown) {
        // @ts-ignore
        throw handleError({error})
      }
    },
    post: async <T extends ApiWithErrorResponse, D = unknown>(
      url: string,
      data: D,
      config: ExtendedAxiosRequestConfig<D>
    ): Promise<AxiosResponse<T>> => {
      try {
        const response = await apiInstance.post(url, data, config);
        return response;
      } catch (error: unknown) {
        // @ts-ignore
        throw handleError({error})
      }
    },
    put: async <T extends ApiWithErrorResponse, D = unknown>(
      url: string,
      data: D,
      config: ExtendedAxiosRequestConfig<D>
    ): Promise<AxiosResponse<T>> => {
      try {
        const response = await apiInstance.put(url, data, config);
        return response;
      } catch (error: unknown) {
        // @ts-ignore
        throw handleError({error})
      }
    },
    patch: async <T extends ApiWithErrorResponse, D = unknown>(
      url: string,
      data: D,
      config: ExtendedAxiosRequestConfig<D>
    ): Promise<AxiosResponse<T>> => {
      try {
        const response = await apiInstance.patch(url, data, config);
        return response;
      } catch (error: unknown) {
        // @ts-ignore
        throw handleError({error})
      }
    },
    delete: async <T extends ApiWithErrorResponse, D = unknown>(
      url: string,
      config: ExtendedAxiosRequestConfig<D>
    ): Promise<AxiosResponse<T>> => {
      try {
        const response = await apiInstance.delete(url, config);
        return response;
      } catch (error: unknown) {
        // @ts-ignore
        throw handleError({error})
      }
    }
  };
};
